import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  get details() {
    const details = this.querySelector('details');

    return details instanceof HTMLDetailsElement ? details : null;
  }

  /** @type {HTMLElement | null} */
  get summary() {
    const summary = this.details?.querySelector('summary');

    return summary instanceof HTMLElement ? summary : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #childObserver = null;

  connectedCallback() {
    // A section re-render or DOM morph can connect this element before its
    // children exist, so wait for the markup instead of giving up on it.
    if (!this.#setup()) this.#waitForDetails();
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller?.abort();
    this.#controller = null;

    this.#childObserver?.disconnect();
    this.#childObserver = null;
  }

  /**
   * Wires up the accordion.
   * @returns {boolean} False while the details markup is still missing.
   */
  #setup() {
    const summary = this.summary;

    if (!summary) return false;

    // A fresh controller per connect. The previous one is aborted on disconnect,
    // and an aborted signal drops every listener it is handed without a word.
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    return true;
  }

  /**
   * Watches for the details markup arriving late, then wires up and stops watching.
   *
   * @returns {void}
   */
  #waitForDetails() {
    this.#childObserver?.disconnect();

    this.#childObserver = new MutationObserver(() => {
      if (!this.#setup()) return;

      this.#childObserver?.disconnect();
      this.#childObserver = null;
    });

    this.#childObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const details = this.details;

    if (!details) return;

    const isMobile = isMobileBreakpoint();

    details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      const details = this.details;

      if (!details) return;

      event.preventDefault();

      details.open = false;
      this.summary?.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
